import React, { useState } from 'react';
import { OC, FONT, CAT_KEYS, CATEGORY } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import OCApi from '../api/client.js';
import { Button, Icon, Input } from './ui.js';

function ModalShell({ theme, onClose, title, children }) {
    const t = useTheme(theme);
    return React.createElement('div', {
        onClick: onClose,
        style: {
            position: 'fixed', inset: 0, zIndex: 95, background: 'rgba(0,0,0,.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }
    },
        React.createElement('div', {
            onClick: (e) => e.stopPropagation(),
            style: {
                width: 520, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto',
                background: t.bg.overlay, border: `1px solid ${t.border.base}`,
                borderRadius: 20, padding: 28, fontFamily: FONT.body, boxShadow: t.shadow.lg,
            }
        },
            React.createElement('h3', {
                style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 20, margin: '0 0 16px', color: t.text.primary }
            }, title),
            children
        )
    );
}

export function CommunityShareModal({ open, onClose }) {
    const s = useStore();
    const t = useTheme(s.theme);
    const [categorySlug, setCategorySlug] = useState('jobs');
    const [title, setTitle] = useState('');
    const [org, setOrg] = useState('');
    const [applyUrl, setApplyUrl] = useState('');
    const [description, setDescription] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!open) return null;

    const submit = async () => {
        if (!s.authed) {
            s.showToast('Log in to share', 'x');
            s.go('login');
            onClose();
            return;
        }
        const cat = s.categories.find((c) => c.slug === categorySlug);
        if (!cat) {
            setError('Categories not loaded');
            return;
        }
        setBusy(true);
        setError('');
        try {
            await OCApi.submitCommunityShare({
                title: title.trim(),
                organization: org.trim(),
                description: description.trim(),
                categoryId: cat.id,
                applyUrl: applyUrl.trim() || undefined,
            });
            s.showToast('Submitted for volunteer review', 'check');
            onClose();
        } catch (err) {
            setError(err.message || 'Could not submit');
        } finally {
            setBusy(false);
        }
    };

    const inputStyle = {
        width: '100%', boxSizing: 'border-box', height: 44, padding: '0 12px',
        borderRadius: 10, border: `1.5px solid ${t.border.base}`, background: t.bg.page,
        fontFamily: FONT.body, fontSize: 14, color: t.text.primary, marginBottom: 12,
    };

    return React.createElement(ModalShell, { theme: s.theme, onClose, title: 'Share an opportunity' },
        React.createElement('p', { style: { fontSize: 14, color: t.text.secondary, margin: '0 0 16px', lineHeight: 1.5 } },
            'Found something worth sharing? Volunteers will review it before it appears in the feed.'
        ),
        React.createElement('select', {
            value: categorySlug,
            onChange: (e) => setCategorySlug(e.target.value),
            style: inputStyle,
        }, CAT_KEYS.map((k) => React.createElement('option', { key: k, value: k }, CATEGORY[k].label))),
        React.createElement('input', { placeholder: 'Title *', value: title, onChange: (e) => setTitle(e.target.value), style: inputStyle }),
        React.createElement('input', { placeholder: 'Organization', value: org, onChange: (e) => setOrg(e.target.value), style: inputStyle }),
        React.createElement('input', { placeholder: 'Apply URL', value: applyUrl, onChange: (e) => setApplyUrl(e.target.value), style: inputStyle }),
        React.createElement('textarea', {
            placeholder: 'Description (deadline, eligibility, why it matters) *',
            value: description, rows: 4, onChange: (e) => setDescription(e.target.value),
            style: { ...inputStyle, height: 'auto', padding: '12px', resize: 'vertical' },
        }),
        error ? React.createElement('div', { style: { color: OC.urgent, fontSize: 13, marginBottom: 10 } }, error) : null,
        React.createElement(Button, { kind: 'primary', size: 'lg', theme: s.theme, fullWidth: true, onClick: submit, disabled: busy }, busy ? 'Submitting…' : 'Submit for review'),
        React.createElement(Button, { kind: 'ghost', size: 'md', theme: s.theme, fullWidth: true, onClick: onClose, style: { marginTop: 8 } }, 'Cancel')
    );
}

export function ReviewerApplyModal({ open, onClose }) {
    const s = useStore();
    const t = useTheme(s.theme);
    const [fullName, setFullName] = useState(s.user?.name || '');
    const [email, setEmail] = useState(s.user?.email || '');
    const [chapter, setChapter] = useState('');
    const [motivation, setMotivation] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!open) return null;

    const submit = async () => {
        setBusy(true);
        setError('');
        try {
            await OCApi.submitReviewerApplication({ fullName, email, chapter, motivation });
            s.showToast('Application received — we will email you', 'check');
            onClose();
        } catch (err) {
            setError(err.message || 'Could not submit');
        } finally {
            setBusy(false);
        }
    };

    return React.createElement(ModalShell, { theme: s.theme, onClose, title: 'Apply to review' },
        React.createElement(Input, { theme: s.theme, label: 'Full name', value: fullName, onChange: setFullName }),
        React.createElement('div', { style: { height: 12 } }),
        React.createElement(Input, { theme: s.theme, label: 'Email', value: email, onChange: setEmail }),
        React.createElement('div', { style: { height: 12 } }),
        React.createElement(Input, { theme: s.theme, label: 'Chapter / region', value: chapter, onChange: setChapter }),
        React.createElement('div', { style: { height: 12 } }),
        React.createElement('label', { style: { fontSize: 13, fontWeight: 600, color: t.text.primary } }, 'Why do you want to review?'),
        React.createElement('textarea', {
            value: motivation, onChange: (e) => setMotivation(e.target.value), rows: 4,
            style: {
                width: '100%', boxSizing: 'border-box', marginTop: 8, padding: 12, borderRadius: 10,
                border: `1.5px solid ${t.border.base}`, background: t.bg.page, fontFamily: FONT.body, fontSize: 14,
            }
        }),
        error ? React.createElement('div', { style: { color: OC.urgent, fontSize: 13, marginTop: 10 } }, error) : null,
        React.createElement(Button, { kind: 'primary', size: 'lg', theme: s.theme, fullWidth: true, onClick: submit, disabled: busy, style: { marginTop: 16 } }, busy ? 'Sending…' : 'Submit application'),
    );
}

export function NewsletterModal({ open, onClose, source = 'community' }) {
    const s = useStore();
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);

    if (!open) return null;

    const submit = async () => {
        setBusy(true);
        try {
            await OCApi.subscribeNewsletter(email, source);
            s.showToast('You are on the list', 'check');
            onClose();
        } catch (err) {
            s.showToast(err.message || 'Could not subscribe', 'x');
        } finally {
            setBusy(false);
        }
    };

    return React.createElement(ModalShell, { theme: s.theme, onClose, title: 'Newsletter' },
        React.createElement(Input, { theme: s.theme, label: 'Email', value: email, onChange: setEmail }),
        React.createElement(Button, { kind: 'primary', size: 'lg', theme: s.theme, fullWidth: true, onClick: submit, disabled: busy, style: { marginTop: 16 } }, 'Subscribe'),
    );
}
